'use client';

import { Button, FieldError, LoadingOverlay, TextInput, toast, useListQuery, useModal } from '@payloadcms/ui';
import { useState } from 'react';

import './styles.scss';

import { EXPORT_METHODS } from '@/components/DataIngestion/_constants/data-ingestion';
import { addExportRecord, getCollectionExportData } from '@/components/DataIngestion/actions/export/export';
import { CreateSheetResult, exportToGoogleSheets } from '@/components/DataIngestion/actions/google-sheet';
import { processExportDataForGoogleSheet } from '@/components/DataIngestion/utils/google-sheet';
import { GoogleSheetMethodBlock } from '@/payload-types';

import ConnectedToGoogleSheets from '../../../components/ConnectedToGoogleSheets';
import GoogleSheetsConnectionManager from '../../../components/GoogleSheetsConnectionManager';
import { EXPORT_JOURNEY_MODAL_SLUG } from '../../ExportModal';

const CONTAINER_CLASS = 'fv-google-sheet-export';

const GoogleSheet = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<CreateSheetResult>();
  const [exportName, setExportName] = useState('');
  const [showExportNameError, setShowExportNameError] = useState(false);

  const { collectionSlug } = useListQuery();
  const { closeModal } = useModal();

  const handleExportSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!exportName) {
      setShowExportNameError(true);
      return;
    }

    setShowExportNameError(false);
    setIsExporting(true);

    try {
      const exportData = await getCollectionExportData({ collectionSlug });
      const googleSheetData = processExportDataForGoogleSheet({ data: exportData, collectionSlug });
      const result = await exportToGoogleSheets({ data: googleSheetData, name: exportName });
      setExportResult(result);

      if (!result.success) {
        toast.error(result.error || 'Failed to export data to Google Sheets');
        return;
      }

      const method: GoogleSheetMethodBlock = {
        sheetId: result.spreadsheetId!,
        blockType: EXPORT_METHODS.GOOGLE_SHEET,
      };

      addExportRecord({ method, name: exportName, targetCollectionSlug: collectionSlug });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  if (isExporting) {
    return <LoadingOverlay loadingText="Exporting data to Google Sheets..." />;
  }

  if (exportResult?.success && exportResult.spreadsheetUrl) {
    return (
      <div className={CONTAINER_CLASS}>
        <h2>Export completed</h2>
        <p>✓ Data exported successfully.</p>
        <div className={`${CONTAINER_CLASS}__actions`}>
          <Button onClick={() => window.open(exportResult.spreadsheetUrl, '_blank')} buttonStyle="primary">
            Open spreadsheet
          </Button>
          <Button onClick={() => closeModal(EXPORT_JOURNEY_MODAL_SLUG)} buttonStyle="secondary">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <GoogleSheetsConnectionManager>
      {({ status, connect }) => (
        <div className={CONTAINER_CLASS}>
          <h2>Export to Google Sheets</h2>
          <ConnectedToGoogleSheets status={status} />

          <form onSubmit={handleExportSubmit}>
            <TextInput
              path="export-name"
              label="Export name"
              required
              value={exportName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExportName(e.target.value)}
              description="Give a name to the export to identify it in the CMS."
              Error={<FieldError path="export-name" message="Export name is required" showError={showExportNameError} />}
            />

            <div className={`${CONTAINER_CLASS}__actions`}>
              <Button type="submit" buttonStyle="primary">
                Export
              </Button>
              <Button onClick={connect} buttonStyle="secondary">
                Sign in with a different account
              </Button>
            </div>
          </form>
        </div>
      )}
    </GoogleSheetsConnectionManager>
  );
};

export default GoogleSheet;
